import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckIcon,
  ArrowLongRightIcon,
} from '@heroicons/react/24/outline';

const renderItalicLastLine = (multiline) => {
  const lines = String(multiline).split('\n');
  if (lines.length === 1) return lines[0];
  const last = lines.pop();
  return (
    <>
      {lines.map((line, i) => (
        <React.Fragment key={i}>
          {line}
          <br />
        </React.Fragment>
      ))}
      <em className="italic font-serif font-medium text-brand-deep">{last}</em>
    </>
  );
};

export const Hero = ({ crumbTrail = [], title, lede, meta = [], wideLede = false }) => (
  <section className="relative overflow-hidden bg-brand px-5 pb-6 pt-5 sm:px-8 lg:px-14 lg:pb-8 lg:pt-6">
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          'radial-gradient(ellipse 60% 70% at 15% 0%, rgba(255,255,255,.45), transparent 60%), radial-gradient(ellipse 80% 50% at 100% 100%, rgba(0,0,0,.07), transparent 60%)',
      }}
    />
    <div className="relative">
      {crumbTrail.length > 0 && (
        <div className="mb-3 inline-flex flex-wrap items-center gap-2 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-ink-2/70">
          <span>there2b</span>
          {crumbTrail.map((c, i) => (
            <React.Fragment key={i}>
              <span>/</span>
              <span>{c.label}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      <h1 className="m-0 font-extrabold leading-[1] tracking-[-0.02em] text-ink-strong text-[32px] sm:text-[40px] lg:text-[52px]">
        {renderItalicLastLine(title)}
      </h1>

      {lede && (
        <p
          className={`mt-2 text-sm leading-[1.5] text-ink-strong/80 lg:text-[15px] ${
            wideLede ? '' : 'max-w-[580px]'
          }`}
        >
          {lede}
        </p>
      )}

      {meta.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3 font-display text-[11px] font-bold uppercase tracking-[0.12em] text-ink-2">
          {meta.map((m, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="h-1 w-1 rounded-full bg-black/50" />}
              {typeof m === 'string' ? (
                <span>{m}</span>
              ) : m.ok ? (
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold normal-case tracking-normal text-emerald-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-700" />
                  {m.ok}
                </span>
              ) : null}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  </section>
);

export const Card = React.forwardRef(({ id, children, className = '' }, ref) => (
  <section
    ref={ref}
    id={id}
    className={`flex flex-col gap-5 rounded-2xl border border-line bg-white p-6 shadow-softer lg:p-8 ${className}`}
    style={{ scrollMarginTop: 80 }}
  >
    {children}
  </section>
));
Card.displayName = 'Card';

export const CardHeader = ({
  num,
  title,
  status,
  badge,
}) => (
  <>
    <div className="flex items-baseline gap-4 pb-1">
      <span className="font-display text-[11.5px] font-bold uppercase tracking-[0.14em] text-muted">
        {num}
      </span>
      <h2 className="m-0 text-[20px] font-extrabold leading-[1.15] tracking-[-0.01em] text-ink-strong lg:text-[24px]">
        {title}
      </h2>
      {/* Sits beside the title rather than out on the right like `status`,
          because it qualifies the section's name ("Payouts, required") instead
          of reporting on its state. */}
      {badge && (
        <span className="inline-flex shrink-0 items-center rounded-full bg-rose-50 px-2.5 py-1 text-[11.5px] font-bold text-rose-600">
          {badge}
        </span>
      )}
      <span className="flex-1" />
      {status && (
        <span
          className={`inline-flex items-center gap-1.5 font-display text-[11px] font-bold uppercase tracking-[0.12em] ${
            status.tone === 'active' ? 'text-brand-hover' : 'text-emerald-700'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              status.tone === 'active' ? 'bg-brand-hover' : 'bg-emerald-700'
            }`}
          />
          {status.label}
        </span>
      )}
    </div>
  </>
);

export const FieldLabel = ({ children, optional }) => (
  <label className="flex items-center gap-2 font-display text-[11.5px] font-bold uppercase tracking-[0.12em] text-ink-2">
    <span>{children}</span>
    {optional && (
      <span className="text-[11px] font-medium normal-case tracking-normal text-muted">
        {optional}
      </span>
    )}
  </label>
);

const baseInputClass =
  'w-full appearance-none rounded-[10px] border border-field-border bg-white px-3.5 py-3 text-[14.5px] text-ink outline-none transition focus:border-ink focus:shadow-[0_0_0_3px_rgba(26,26,31,0.08)]';

export const Field = ({
  label,
  hint,
  optional,
  error,
  icon,
  suffix,
  children,
}) => {
  const child = React.Children.only(children);
  const childClass = child.props.className || '';
  const merged = [
    baseInputClass,
    icon ? 'pl-11' : '',
    suffix ? 'pr-11' : '',
    childClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="flex flex-col gap-[7px]">
      {label && <FieldLabel optional={optional}>{label}</FieldLabel>}
      <div className="relative">
        {icon && (
          <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted">
            {icon}
          </span>
        )}
        {React.cloneElement(child, { className: merged })}
        {suffix && (
          <button
            type="button"
            onClick={suffix.onClick}
            aria-label={suffix.label}
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-ink"
          >
            {suffix.icon}
          </button>
        )}
      </div>
      {hint && !error && (
        <p className="text-xs leading-[1.4] text-muted">{hint}</p>
      )}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

export const inputBaseClass = baseInputClass;

export const SuccessView = ({ email, onContinue }) => {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-[720px] px-5 py-16 text-center lg:px-14 lg:py-24">
      <div className="mx-auto mb-7 grid h-[88px] w-[88px] place-items-center rounded-full bg-brand text-ink shadow-[0_8px_28px_-10px_rgba(255,189,89,0.7)]">
        <CheckIcon className="h-10 w-10" strokeWidth={2.2} />
      </div>
      <h2 className="m-0 mb-4 text-[34px] font-extrabold leading-[1.05] tracking-[-0.02em] text-ink-strong lg:text-[44px]">
        {t('register.success_title', 'Check your\ninbox.')
          .split('\n')
          .map((line, i, arr) =>
            i === arr.length - 1 ? (
              <em
                key={i}
                className="italic font-serif font-medium text-brand-deep"
              >
                {line}
              </em>
            ) : (
              <React.Fragment key={i}>
                {line}
                <br />
              </React.Fragment>
            )
          )}
      </h2>
      <p className="mx-auto mb-9 max-w-[520px] text-base leading-[1.5] text-ink-strong/75 lg:text-[17px]">
        {t(
          'register.success_body',
          "We've sent a confirmation link to {{email}}."
        )
          .split(/<strong>(.*?)<\/strong>/)
          .map((part, i) =>
            i % 2 === 1 ? (
              <strong
                key={i}
                className="font-extrabold not-italic text-ink-strong"
              >
                {part.replace('{{email}}', email)}
              </strong>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
      </p>
      <div className="flex flex-col gap-2.5 sm:flex-row sm:justify-center">
        <button
          type="button"
          onClick={onContinue}
          className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-ink bg-ink px-5 py-3 text-sm font-semibold text-white hover:bg-ink/90"
        >
          {t('register.success_continue', 'Continue to login')}
          <ArrowLongRightIcon className="h-4 w-4" strokeWidth={2} />
        </button>
        <a
          href="https://mail.google.com"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-[10px] border border-line-strong bg-transparent px-5 py-3 text-sm font-semibold text-ink hover:bg-paper-2"
        >
          {t('register.success_open_mail', 'Open Gmail')}
        </a>
      </div>
      <p className="mt-7 text-[13px] text-muted">
        {t(
          'register.success_help',
          "Didn't get it? Check spam, or contact support."
        )
          .split(/<a>(.*?)<\/a>/)
          .map((part, i) =>
            i % 2 === 1 ? (
              <a
                key={i}
                href="mailto:support@there2b.com"
                className="font-semibold text-ink"
              >
                {part}
              </a>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
      </p>
    </div>
  );
};
